import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, FilterBar, ModalVariables, SearchComponent, StartPage } from "../../../components";
import { useAuth } from "../../../context/user.context";
import { icons } from "../../../icons";
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import { useNavigate } from "react-router-dom";
import { useUi } from "../../../context/ui.context";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { clientService } from "../../../api";
import { compareArrays } from "../../../utils/findIntersection";
import useUpdateEffect from "../../../hooks/useUpdateEffect";
import { usePageTitle } from "../../../context/navigationContext";
const ROLE_FILTER_KEYS = ['created_at', 'updated_at', 'owner', 'parent', 'public', 'default'];
const DEFAULT_ORDERING = '-created_at';

const Roles:FC = () => {
  const {user, updateUser} = useAuth()
  const [rows, setRows] = useState<any[]>([])
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [selectedRole, setSelectedRole] = useState<any>()
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [filtering, setFiltering] = useQueryParam('filtering', StringParam);
  const {viewAlert} = useUi()
  const [paginationData, setPaginationData] = useState<any>()
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [alertTitle, setAlertTitle] = useState<any>('')
  const [alertSubTitle, setAlertSubTitle] = useState<any[]>([])
  const [t] = useTranslation()
  usePageTitle(t("PAGE_TITLE_ROLES"));
  const [allChecked, setAllChecked] = useState<boolean>(false)
  const [checked, setChecked] = useState<any[]>([])
  const [filterOptions, setFilterOptions] = useState<any>()
  const normalizedFilterOptions = useMemo(() => {
    const base: Record<string, any> = {};
    if (filterOptions) {
      ROLE_FILTER_KEYS.forEach((key) => {
        if (Array.isArray(filterOptions?.[key]) && filterOptions?.[key]?.length) {
          base[key] = filterOptions[key];
        }
      });
    }
    const booleanOptions = [
      { display: t("YES"), value: 'true' },
      { display: t("NO"), value: 'false' },
    ];
    ['public', 'default'].forEach((key) => {
      if (!base[key]) {
        base[key] = booleanOptions;
      }
    });
    return base;
  }, [filterOptions, t]);
  const handleResetFilters = () => {
    setSearchString(undefined);
    setFiltering(undefined);
    setOrdering(DEFAULT_ORDERING);
    setPage(1);
    setAllChecked(false);
    setChecked([]);
  }

  const editChecked = (id:any, isAll?:boolean) => {
    if(id === null) {
      setChecked([])
      setAllChecked(false)
      return
    }
    const founded = checked.find((item:any) => item.id === id?.id)
    if(founded) {
      if(isAll){
        return
      } else {
        setChecked((prev:any) => {
          return prev.filter((item:any) => item.id !== id?.id)
        })
        setAllChecked(false)
      }
    } else {
      setChecked((prev) => [...prev, id])
    }
  }

  useEffect(() => {
    if(compareArrays(rows, checked) && checked?.length !== 0){
      setAllChecked(true)
    }
  },[checked])

  const updateList = async () => {
    try{
      const res = await clientService.getUserRoles({
        page:page||1,
        ordering: ordering || DEFAULT_ORDERING,
        pageSize:pageSize||10,
        search:searchString||'',
        filtering: filtering || '',
      })
      let data = res.data.objects.map((role:any) => {
        return {
        ...role,
        id:role.id,
        name:role.name,
        display_name:role.display_name,
        navigate:`${role.id}`,
        is_public: role.public ? t("YES") : t("NO"),
        is_default: role.default ? t("YES") : t("NO"),
        created_at:timeConverterMMDDYYHHMM(role.created_at),
        updated_at:timeConverterMMDDYYHHMM(role.updated_at),
        parent_name: role.parent_display || '-',
        owner_name: role.owner_display || '-',
        default: role.default,
        public: role.public,
      }})
      setPaginationData(res.data)
      setFilterOptions(res.data?.filter_options);
      setRows(data)
      setPaginationData(res.data);
      setPageLoading(false)

    } catch(e:any){
      if(e?.response?.data?.detail){
        viewAlert({
          severity:'error',
          message:e?.response?.data?.detail
        })
      }
      setPageLoading(true)

    }
    
  }
  const navigate = useNavigate()

  useEffect(() => {
    updateList()
  },[page, ordering, pageSize, searchString, filtering])
  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, filtering])
  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);

  const handleSetPublic = async (role:any) => {
    setLoading(true)
    try {
      await clientService.setRolePublic(role.id)
      viewAlert({
        severity:'success',
        message:t("ROLE_SET_PUBLIC_SUCCESS")
      })
      await updateList()
    } catch (e:any) {
      if(e?.response?.data?.detail){
        viewAlert({
          severity:'error',
          message:e?.response?.data?.detail
        })
      }
    }
    setLoading(false)
  }

  const handleSetPrivate = async (role:any) => {
    setLoading(true)
    try {
      await clientService.setRolePrivate(role.id)
      viewAlert({
        severity:'success',
        message:t("ROLE_SET_PRIVATE_SUCCESS")
      })
      await updateList()
    } catch (e:any) {
      if(e?.response?.data?.detail){
        viewAlert({
          severity:'error',
          message:e?.response?.data?.detail
        })
      }
    }
    setLoading(false)
  }

  const openDeleteModal = (items:any[], selectedItem?:any) => {
    if (!items.length) return
    setSelectedRole(selectedItem)
    setModalType('delete')
    setAlertTitle(items.length === 1 ? t("ROLE") : t("ROLES"))
    setAlertSubTitle(items.map((item:any) => ({
      ...item,
      name: item.display_name || item.name || item.id,
    })))
    setViewModal(true)
    setActiveAnimation(true)
  }

  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
    setSelectedRole(undefined)
  }

  const closeModal = async () => {
    setLoading(true)
    try{
      if(selectedRole) {
        await clientService.deleteUserRole(selectedRole.id)
        viewAlert({
          severity:'success',
          message:t("ROLE_DELETED_SUCCESSFULLY")
        })
        await updateList()
        setChecked([])
        setAllChecked(false)
      } else if(allChecked) {
        let promises:any[] = []
        await rows.map((item:any) => {
          let promise = clientService.deleteUserRole(item.id)
          promises.push(promise)
        })
        await Promise.all(promises)
        viewAlert({
          severity:'success',
          message:t("ROLES_DELETED_SUCCESSFULLY")
        })
        await updateList()
        await setAllChecked(false)
        await setChecked([])
      } else if (checked?.length > 0){
        let promises:any[] = []
        await checked.map((item:any) => {
          let promise = clientService.deleteUserRole(item.id)
          promises.push(promise)
        })
        await Promise.all(promises)
        viewAlert({
          severity:'success',
          message:t("ROLES_DELETED_SUCCESSFULLY")
        })
        await updateList()
        await setChecked([])
      }
    } catch (e:any) {
      if(e?.response?.data?.detail){
        viewAlert({
          severity:'error',
          message:e?.response?.data?.detail
        })
      }
    } finally {
      setViewModal(false)
      setActiveAnimation(false)
      setSelectedRole(undefined)
      setLoading(false)
    }
  }

  const items = [
    {
      title:t("EDIT"),
      id:'edit',
      icon:icons.Edit,
      onClick: (item:any) => navigate(`${item.id}/edit`),
      disabled: (item:any) => item.default,
      divider:false
    },
    {
      title:t("DELETE"),
      id:'delete',
      onClick:(item:any) => {
        openDeleteModal([item], item)
      },
      disabled: (item:any) => item.default,
      icon:icons.DeleteIcon,
      divider:false
    },
    {
      title:t("SET_PUBLIC"),
      id:'set-public',
      icon:icons.EyeIc,
      onClick:(item:any) => handleSetPublic(item),
      disabled: (item:any) => item.public,
      divider:false
    },
    {
      title:t("SET_PRIVATE"),
      id:'set-private',
      icon:icons.HideEyeBlue,
      onClick:(item:any) => handleSetPrivate(item),
      disabled: (item:any) => !item.public,
      divider:false
    },
  ]

  const columns = [
    {
      title:t("ID"),
      key:"id",
      sorted:true,
    },
    {
      title:t("NAME"),
      key:'display_name',
      sorted:true,
    },
    {
      title:t("PUBLIC"),
      key:"is_public"
    },
    {
      title:t("CREATED_AT"),
      key:"created_at",
      sorted:true,
    },
    {
      title:t("UPDATED_AT"),
      key:"updated_at",
      sorted:true,
    },
    {
      title:t("PARENT"),
      key:"parent_name",
      sorted:true,
    },
    {
      title:t("OWNER"),
      key:"owner_name",
      sorted:true,
    },
    {
      title:"",
      key:'props'
    }
  ]
  return(
    <>
      <StartPage
        title={`${t("ROLES")}`}
        noResourceText={`${t("NO_ROLES")}`}
        createResourceButton={`${t('CREATE_ROLE')}`}
        createResourceLink="/clients-users/roles/create"
        haveResource={rows.length > 0}
        withoutCreateResourceButton
        rightCustomCreateButton={
          <div style={{display:'flex', flexDirection:'column', gap:'10px', width:'100%'}}>
            <div style={{display:'flex', alignItems:'center', gap:'12px', width:'100%'}}>
              <Button
                title={t('CREATE_ROLE')}
                onClick={() => navigate('/clients-users/roles/create')}
                variant='primary'
                size='custom'
                customStyles={{height:'40px', whiteSpace:'nowrap'}}
              />
              <div style={{flex:1, minWidth:'200px'}}>
                <SearchComponent />
              </div>
              <Button
                onClick={() => {
                  openDeleteModal(allChecked ? rows : checked)
                }}
                variant='icon'
                size='display'
                test_id='button-delete'
                disabled={!allChecked && checked?.length === 0 }
              >
                <img src={icons.DeleteIcon}/>
              </Button>
              <Button
                title={t("CLEAR_FILTERS")}
                onClick={handleResetFilters}
                variant='stroke'
                size='display'
                disabled={
                  (!filtering || filtering === '') &&
                  !searchString &&
                  ordering === DEFAULT_ORDERING
                }
              />
            </div>
            {normalizedFilterOptions ? (
              <FilterBar filterOptions={normalizedFilterOptions} />
            ) : null}
          </div>
        }
        pageLoading={pageLoading}
        tableData={{
          columns:columns,
          data:rows,
          props:items,
          allChecked:allChecked,
          setAllChecked:setAllChecked,
          checked:checked,
          setChecked:editChecked,
          paginationData:paginationData,
          updateData:updateList
        }}
      />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        loading={loading}
      />
    </>
  )
}

export default Roles
